import { BigNumber } from '@ethersproject/bignumber';
import { parseUnits } from '@ethersproject/units';
import { useQuery } from '@tanstack/react-query';
import { useVenoSDK } from '@ui/providers/VenoSDKProvider';
import { getBoostTokens } from '@ui/queries/sdk';
import { currentWallet } from '@veno-app/wallet';

const { useAccount } = currentWallet;

const thresholdNumbers =
  process.env.NEXT_PUBLIC_DEFAULT_CRONOS_NETWORK === 'cronos_testnet'
    ? [
        0.00001, 45_563, 91_125, 227_813, 455_625, 683_438, 911_250, 2_278_125,
        4_556_250, 9_112_500, 22_781_250, 45_562_500, 68_343_750, 91_125_000,
        182_250_000,
      ]
    : [
        0.001, 45_562_500, 91_125_000, 227_812_500, 455_625_000, 683_437_500,
        911_250_000, 2_278_125_000, 4_556_250_000, 9_112_500_000,
        22_781_250_000, 45_562_500_000, 68_343_750_000, 91_125_000_000,
        182_250_000_000,
      ];

const thresholds = thresholdNumbers.map((n) => parseUnits(n.toString()));

const useBoostTokens = () => {
  const vaultMultiplier = 9600;
  const sdk = useVenoSDK();
  const account = useAccount();
  const query = getBoostTokens(sdk, account ?? '');
  const { data: tokens, isLoading } = useQuery(query.queryKey, query.queryFn, {
    enabled: Boolean(account),
  });

  if (!tokens) return { isLoading };

  const index = thresholds.findIndex((threshold) => threshold.gte(tokens));

  if (index < 0) {
    return {
      isLoading,
      progress: 1,
      isMax: true,
    };
  }

  if (thresholds[index].eq(tokens)) {
    const nextAmount =
      index + 1 < thresholds.length ? thresholds[index + 1] : undefined;
    const remainingBoostTokens = nextAmount?.sub(thresholds[index]);
    return {
      isLoading,
      nextAmount,
      remainingIn96: remainingBoostTokens
        ?.div(vaultMultiplier)
        .add(remainingBoostTokens.mod(vaultMultiplier).gt(0) ? 1 : 0),
      progress: 0,
      isMax: false,
    };
  }

  const nextAmount = thresholds[index];
  const prevAmount = index > 1 ? thresholds[index - 1] : BigNumber.from(0);
  const remainingBoostTokens = nextAmount.sub(tokens);
  const remainingIn96 = remainingBoostTokens
    .div(vaultMultiplier)
    .add(remainingBoostTokens.mod(vaultMultiplier).gt(0) ? 1 : 0);
  const factor = 100_000_000;
  return {
    isLoading,
    nextAmount,
    remainingIn96,
    progress:
      tokens
        .sub(prevAmount)
        .mul(factor)
        .div(nextAmount.sub(prevAmount))
        .toNumber() / factor,
    isMax: false,
  };
};

export default useBoostTokens;
